import { IPagination } from 'common/components/pagination/Pagination';
import DataTable from 'common/components/table/DataTable';
import { formatDateFromSeconds } from 'common/services/date/formatDate';
import ClientFacingStatus from 'domain/enums/ClientFacingStatus';
import RejectionStatusCd from 'domain/enums/RejectionStatusCd';
import RejectionTypeCd from 'domain/enums/RejectionTypeCd';
import { Application } from 'domain/types/Application';
import { addToCart, removeFromCart } from 'features/cart/CartSlice';
import { fetchProfileVerifications } from 'features/verifications/VerificationsSlice';
import { useEffect } from 'react';
import useThunkDispatch from '../../../common/hooks/useThunkDispatch';
import useTypedSelector from '../../../common/hooks/useTypedSelector';
import Actions from './Actions';
import { GroupKeyLink } from './GroupKeyLink';

interface IApplicationTable {
  pagination: IPagination;
  data: Application[];
  onSort: (sortBy: string, sortDir: string) => void;
  onSelect: (checked: boolean, row: any, selectedRows: number[]) => void;
}

const ApplicationTable = (props: IApplicationTable) => {
  const dispatch = useThunkDispatch();

  useEffect(() => {
    dispatch(fetchProfileVerifications());
  }, [dispatch]);

  const { groupRefKeys } = useTypedSelector((state) => ({
    groupRefKeys: state.cart.groupRefKeys,
  }));

  const isDeniedAndHasUnresolvedRejection = (
    app: Application,
    typeCd: RejectionTypeCd
  ) =>
    app.statusCd === ClientFacingStatus.PRECHECK_FAILED &&
    app.rejectionReasons.some(
      (rr) =>
        rr.typeCd === typeCd && rr.statusCd === RejectionStatusCd.NOT_RESOLVED
    );

  return (
    <DataTable
      responsive
      columns={[
        {
          name: 'Reference Number',
          render: (row) => <GroupKeyLink groupKey={row.groupRefKey} />,
        },
        {
          name: 'Address',
          render: (row) => (
            <div style={{ fontSize: '1.4rem' }}>
              {row.address}
              {/* ({row.room || 'Entire dwelling'}) */}
            </div>
          ),
          onSort: (sortDir) => props.onSort('ADDRESS', sortDir),
        },
        {
          name: 'Submitted On',
          render: (row) => (
            <div>{formatDateFromSeconds(row.submittedDate)}</div>
          ),
          onSort: (sortDir) => props.onSort('SUBMITTED_DATE', sortDir),
        },
        {
          name: 'Status',
          render: (row) => <div>{row.statusDescr}</div>,
        },
        {
          name: 'Action Required',
          render: (data) => (
            <Actions
              rejections={data.rejectionReasons}
              shouldResubmit={
                data.statusCd === ClientFacingStatus.PRECHECK_FAILED &&
                data.rejectionReasons.every(
                  (rr) => rr.statusCd !== RejectionStatusCd.NOT_RESOLVED
                )
              }
              shouldReuploadID={isDeniedAndHasUnresolvedRejection(
                data,
                RejectionTypeCd.IDENTITY
              )}
              shouldResuploadPrimaryResidence={isDeniedAndHasUnresolvedRejection(
                data,
                RejectionTypeCd.PRIMARY_RESIDENCE
              )}
              shouldResuploadPrimaryResidence2={isDeniedAndHasUnresolvedRejection(
                data,
                RejectionTypeCd.PRIMARY_RESIDENCE_02
              )}
              shouldSubmitShuol={isDeniedAndHasUnresolvedRejection(
                data,
                RejectionTypeCd.SHUOL
              )}
              shouldCheckout={
                data.canCheckout && !groupRefKeys.includes(data.groupRefKey)
              }
              onCheckout={() => dispatch(addToCart(data.groupRefKey))}
              isInCart={groupRefKeys.includes(data.groupRefKey)}
              onRemoveFromCart={() =>
                dispatch(removeFromCart(data.groupRefKey))
              }
              hasCityDebt={isDeniedAndHasUnresolvedRejection(
                data,
                RejectionTypeCd.CITY_DEBT
              )}
              isRevoked={isDeniedAndHasUnresolvedRejection(
                data,
                RejectionTypeCd.REVOKED
              )}
              groupRefKey={data.groupRefKey}
              isRegistrationAction={false}
              canRequestRenewal={false}
            />
          ),
        },
      ]}
      data={props.data}
      keyField='id'
      selectable
      onSelect={(checked, row, selectedRows) => {
        props.onSelect(checked, row, selectedRows);
      }}
      pagination={props.pagination}
    />
  );
};

export default ApplicationTable;
